import type { User } from '@/types/auth';
import { isDemoModeEnabled } from '@/demo/config';
import { readCookieConsent } from './cookieConsent';
import { readAttributionCookie, sendServerEvent } from './attribution';
import { clearPendingSignupConversions, flushPendingSignupConversions, queueSignupConversion } from './signupConversions';
import { getAnalyticsPageMetadata } from './analytics/pageMetadata';
import {
  getAnalyticsClickEventName,
  getAnalyticsElementLabel,
  getAnalyticsElementSection,
} from './analytics/clickMetadata';
import { getAnalyticsCountryProperties } from './analytics/countryMetadata';

type MixpanelClient = typeof import('mixpanel-browser')['default'];

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
const GA_DEBUG_MODE = import.meta.env.VITE_GA_DEBUG_MODE?.trim().toLowerCase() === 'true';
const GTM_CONTAINER_ID = import.meta.env.VITE_GTM_CONTAINER_ID?.trim();
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID?.trim();
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN?.trim();
const MIXPANEL_DEBUG = import.meta.env.VITE_MIXPANEL_DEBUG?.trim().toLowerCase() === 'true';
const MIXPANEL_API_HOST = import.meta.env.VITE_MIXPANEL_API_HOST?.trim();
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID?.trim();
const LINKEDIN_PARTNER_ID = import.meta.env.VITE_LINKEDIN_PARTNER_ID?.trim();
const LINKEDIN_BOOK_DEMO_CONVERSION_ID = import.meta.env.VITE_LINKEDIN_BOOK_DEMO_CONVERSION_ID?.trim();
const LINKEDIN_SIGNUP_CONVERSION_ID = import.meta.env.VITE_LINKEDIN_SIGNUP_CONVERSION_ID?.trim();
const MIXPANEL_BATCH_FLUSH_INTERVAL_MS = 3000;
const MIXPANEL_BATCH_SIZE = 20;
const TRACK_DEDUPE_WINDOW_MS = 1000;
const APP_ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT?.trim() || import.meta.env.MODE || 'development';
const APP_VERSION =
  import.meta.env.VITE_APP_VERSION?.trim() ||
  import.meta.env.VITE_APP_BUILD_VERSION?.trim() ||
  '0.1.0';

type GtagFunction = (...args: unknown[]) => void;
type ClarityFunction = ((...args: unknown[]) => void) & { q?: unknown[] };
type MetaPixelFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  push?: MetaPixelFunction;
  queue?: unknown[];
  version?: string;
};
type LinkedInTrackFunction = ((action: string, data?: Record<string, unknown>) => void) & {
  q?: unknown[];
};
type AnalyticsValue = string | number | boolean | null | undefined | string[] | number[] | boolean[];
export type AnalyticsProperties = Record<string, AnalyticsValue>;

export type AnalyticsEventName =
  | 'Page Viewed'
  | 'Rage Clicked'
  | 'Dead Clicked'
  | 'JavaScript Error'
  | 'API Request Failed'
  | 'Landing CTA Clicked'
  | 'Landing Contact Form Submitted'
  | 'Signup Started'
  | 'Signup Submitted'
  | 'Signup Completed'
  | 'Login OTP Requested'
  | 'Login Completed'
  | 'Logout Completed'
  | 'Client Onboarding: Schedule Appointment Clicked'
  | 'Client Onboarding: Add Invoice Clicked'
  | 'Client Onboarding: Attach Questionnaire Clicked'
  | 'Client Created'
  | 'Questionnaire Assigned'
  | 'Appointment Created'
  | 'Invoice Created'
  | 'Client Onboarding: Add Health Measurements Clicked'
  | 'Client Onboarding: Review Invitation Clicked'
  | 'Open Client Onboarding Clicked'
  | 'Client Onboarding: Create Client Clicked'
  | 'Admin Client Invitations: Back Clicked'
  | 'Client Appointments: Schedule Appointment Clicked'
  | 'Client Onboarding: Exit Setup Clicked'
  | 'Client Onboarding: Back from Health Measurements Clicked'
  | 'Client Onboarding: Back from Invitation Review Clicked'
  | 'Client Onboarding: Invite Client to Complete Profile Clicked'
  | 'Client Onboarding Follow Up Selected'
  | 'Client Onboarding Back Clicked'
  | 'Client Onboarding Exited'
  | 'Client Onboarding Completed'
  | 'Client Details Updated'
  | 'Client Note Added'
  | 'Client Lab Report Extraction Started'
  | 'Client Lab Report Updated'
  | 'Client Lab Report Notes Updated'
  | 'Client Lab Report Deleted'
  | 'Add Meal Plan Clicked'
  | 'Meal Plan Type Selected'
  | 'AI Meal Plan Setup Viewed'
  | 'AI Meal Plan Setup Started'
  | 'AI Meal Plan Client Selected'
  | 'AI Meal Plan Client Goals Edit Clicked'
  | 'AI Meal Plan Client Goals Saved'
  | 'AI Meal Plan Dietary Preferences Edit Clicked'
  | 'AI Meal Plan Dietary Preferences Saved'
  | 'AI Meal Plan Use Client Meal Slots Enabled'
  | 'AI Meal Plan Use Client Meal Slots Disabled'
  | 'AI Meal Plan Slot Added'
  | 'AI Meal Plan Slot Removed'
  | 'AI Meal Plan Model Selected'
  | 'AI Meal Plan Back Clicked'
  | 'AI Meal Plan Cancel Clicked'
  | 'AI Meal Plan Start Date Picker Clicked'
  | 'AI Meal Plan End Date Picker Clicked'
  | 'AI Meal Plan Generate Clicked'
  | 'AI Meal Plan Single Slot Warning Cancel Clicked'
  | 'AI Meal Plan Single Slot Warning Continue Clicked'
  | 'AI Meal Plan Setup Completed'
  | 'Manual Meal Plan Setup Viewed'
  | 'Manual Meal Plan Setup Started'
  | 'Manual Meal Plan Client Selected'
  | 'Manual Meal Plan Client Goals Edit Clicked'
  | 'Manual Meal Plan Client Goals Saved'
  | 'Manual Meal Plan Dietary Preferences Edit Clicked'
  | 'Manual Meal Plan Dietary Preferences Saved'
  | 'Manual Meal Plan Slot Added'
  | 'Manual Meal Plan Slot Removed'
  | 'Manual Meal Plan Back Clicked'
  | 'Manual Meal Plan Cancel Clicked'
  | 'Manual Meal Plan Create Clicked'
  | 'Manual Meal Plan Setup Completed'
  | 'Manual Meal Plan Created'
  | 'Automated Meal Plan Generation Started'
  | 'AI Meal Plan Generation Started'
  | 'Meal Plan Draft Created'
  | 'Meal Plan Draft Updated'
  | 'AI Meal Plan Draft Updated'
  | 'AI Meal Plan Shuffled'
  | 'AI Meal Plan Smart Edit Applied'
  | 'Meal Plan Item Added'
  | 'Meal Plan Item Updated'
  | 'Meal Plan Item Removed'
  | 'Meal Plan Recipe Added'
  | 'Meal Plan Recipe Removed'
  | 'Meal Plan Slot Added'
  | 'Meal Plan Day Copied'
  | 'Meal Plan Day Removed'
  | 'Meal Plan Day Renamed'
  | 'Meal Plan Dates Changed'
  | 'Meal Plan Reordered'
  | 'Meal Plan Template Saved'
  | 'Meal Slot Template Saved'
  | 'Meal Plan Template Imported'
  | 'Meal Plan Template Applied'
  | 'Meal Plan Finalized'
  | 'Meal Plan Template Save Clicked'
  | 'Meal Plan Template Import Clicked'
  | 'Meal Plan Day Copy Clicked'
  | 'Meal Plan Finalize Clicked'
  | 'Meal Plan Day Delete Clicked'
  | 'Meal Plan: View Nutrition Analysis Clicked'
  | 'Meal Plan: View Detailed Report Clicked'
  | 'Cohort Meal Plan Created'
  | 'Cohort Meal Plan Updated'
  | 'Cohort Meal Plan Deleted'
  | 'Cohort Meal Plan Assigned'
  | 'Practitioner Profile Saved'
  | 'Practice Information Saved'
  | 'Profile Preferences Saved'
  | 'Settings Profile Photo Upload Started'
  | 'Settings Profile Photo Deleted'
  | 'Invoice Settings Saved'
  | 'Weekly Availability Saved'
  | 'Recurring Availability Break Created'
  | 'Recurring Availability Break Updated'
  | 'Recurring Availability Break Deleted'
  | 'Settings Time Off Created'
  | 'Settings Time Off Deleted'
  | 'Settings Consultation Type Created'
  | 'Settings Consultation Type Updated'
  | 'Settings Consultation Type Deleted'
  | 'Settings Password Changed';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFunction;
    clarity?: ClarityFunction;
    fbq?: MetaPixelFunction;
    lintrk?: LinkedInTrackFunction;
    _linkedin_data_partner_ids?: string[];
    _linkedin_partner_id?: string;
  }
}

interface ApiTrackingInput {
  durationMs?: number;
  method?: string;
  status?: number;
  url?: string;
}

interface ApiFailureTrackingInput extends ApiTrackingInput {
  code?: string;
  message?: string;
}

let googleAnalyticsConfigured = false;
let googleConsentDefaultSet = false;
let metaPixelConfigured = false;
let mixpanel: MixpanelClient | null = null;
let mixpanelInitialized = false;
let mixpanelImport: Promise<void> | null = null;
const pendingMixpanelCalls: Array<(client: MixpanelClient) => void> = [];
const MAX_PENDING_MIXPANEL_CALLS = 100;
let globalListenersBound = false;
let missingMixpanelTokenWarned = false;
let lastTrackedGooglePageKey = '';
let lastTrackedMetaPageKey = '';
let lastTrackedPageKey = '';
let lastTrackedReferrer = '';
let lastTrackedEventSignature = '';
let lastTrackedEventAt = 0;
let lastIdentifiedUserSignature = '';
let pendingIdentifiedUserSignature = '';
let lastClickSignature = '';
let lastClickAt = 0;
let domMutationVersion = 0;
let clickBurst:
  | {
      count: number;
      firstAt: number;
      lastAt: number;
      lastX: number;
      lastY: number;
      signature: string;
      tracked: boolean;
    }
  | null = null;

const EMAIL_REGEX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_REGEX = /(?:\+?\d[\d\s().-]{7,}\d)/g;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const HEX_ID_REGEX = /^[0-9a-f]{16,}$/i;
const NUMERIC_ID_REGEX = /^\d+$/;
const LONG_TOKEN_REGEX = /^[A-Za-z0-9_-]{18,}$/;
const SAFE_QUERY_KEYS = new Set([
  'day',
  'from',
  'section',
  'source',
  'status',
  'tab',
  'type',
  'utm_campaign',
  'utm_medium',
  'utm_source',
  'view',
]);

const appendScriptOnce = (id: string, src: string) => {
  if (document.getElementById(id)) {
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const sanitizeText = (value: unknown, fallback = 'Unknown'): string => {
  const raw = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!raw) {
    return fallback;
  }

  const redacted = raw
    .replace(EMAIL_REGEX, '[email]')
    .replace(PHONE_REGEX, '[phone]');

  return redacted.length > 120 ? `${redacted.slice(0, 117)}...` : redacted;
};

const toSnakeCase = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'unknown';

const decodePathSegment = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

const isDynamicPathSegment = (segment: string): boolean => {
  const decoded = decodePathSegment(segment);
  return (
    UUID_REGEX.test(decoded) ||
    HEX_ID_REGEX.test(decoded) ||
    NUMERIC_ID_REGEX.test(decoded) ||
    LONG_TOKEN_REGEX.test(decoded)
  );
};

const sanitizePathname = (pathname: string): string => {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return normalizedPath
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      return isDynamicPathSegment(segment) ? ':id' : encodeURIComponent(decodePathSegment(segment));
    })
    .join('/');
};

const sanitizeSearchParams = (search: string): string => {
  const params = new URLSearchParams(search);
  const sanitized = new URLSearchParams();

  params.forEach((value, key) => {
    if (!SAFE_QUERY_KEYS.has(key)) {
      sanitized.set(key, '__present');
      return;
    }

    if (key === 'from' && value.startsWith('/')) {
      const [fromPath, fromSearch = ''] = value.split('?');
      sanitized.set(key, `${sanitizePathname(fromPath)}${sanitizeSearchParams(fromSearch ? `?${fromSearch}` : '')}`);
      return;
    }

    sanitized.set(key, sanitizeText(value, 'unknown').slice(0, 80));
  });

  const value = sanitized.toString();
  return value ? `?${value}` : '';
};

const getSanitizedPath = (pathname = window.location.pathname, search = window.location.search): string => {
  const { pageRoute } = getAnalyticsPageMetadata(pathname, search);
  return `${pageRoute === '*' ? sanitizePathname(pathname) : pageRoute}${sanitizeSearchParams(search)}`;
};

const getCurrentPageMetadata = () => getAnalyticsPageMetadata(
  window.location.pathname, window.location.search, isDemoModeEnabled(),
);

const getPageViewParams = () => ({
  page_path: getSanitizedPath(),
  page_location: `${window.location.origin}${getSanitizedPath()}`,
  page_title: getCurrentPageMetadata().pageName,
});

const getDeviceType = (): string => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const getPageQueryKeys = (): string[] => {
  const params = new URLSearchParams(window.location.search);
  return Array.from(new Set(Array.from(params.keys()).sort()));
};

const sanitizeReferrer = (referrer: string): string => {
  if (!referrer) {
    return '';
  }

  try {
    const parsed = new URL(referrer, window.location.origin);
    if (parsed.origin === window.location.origin) {
      return `${sanitizePathname(parsed.pathname)}${sanitizeSearchParams(parsed.search)}`;
    }
    return parsed.hostname;
  } catch {
    return sanitizeText(referrer, '');
  }
};

const getCommonEventProperties = (): AnalyticsProperties => ({
  app_name: 'CalorieScience',
  app_platform: 'web',
  app_environment: APP_ENVIRONMENT,
  app_version: APP_VERSION,
  analytics_schema_version: 3,
  device_type: getDeviceType(),
});

const getAttributionClickSources = (): string[] => {
  const attribution = readAttributionCookie();
  if (!attribution) return [];

  return [
    attribution.li_fat_id ? 'linkedin' : '',
    attribution.fbclid ? 'meta' : '',
    attribution.gclid ? 'google_ads' : '',
    attribution.ttclid ? 'tiktok' : '',
    attribution.msclkid ? 'bing' : '',
    attribution.rdt_cid ? 'reddit' : '',
  ].filter(Boolean);
};

const getAttributionSource = (): string | undefined => {
  const attribution = readAttributionCookie();
  if (!attribution) return undefined;

  const utmSource = attribution.utm_source?.trim();
  if (utmSource) return utmSource.toLowerCase();

  return getAttributionClickSources()[0];
};

const getAttributionEventProperties = (prefix = 'attribution'): AnalyticsProperties => {
  const attribution = readAttributionCookie();
  if (!attribution) return {};

  const clickSources = getAttributionClickSources();
  const source = getAttributionSource();

  return {
    [`${prefix}_source`]: source,
    [`${prefix}_medium`]: attribution.utm_medium,
    [`${prefix}_campaign`]: attribution.utm_campaign,
    [`${prefix}_content`]: attribution.utm_content,
    [`${prefix}_term`]: attribution.utm_term,
    [`${prefix}_click_sources`]: clickSources.length ? clickSources : undefined,
    [`${prefix}_has_click_id`]: clickSources.length > 0,
    [`${prefix}_first_touch_at`]: attribution.firstTouchAt,
    [`${prefix}_last_touch_at`]: attribution.lastTouchAt,
  };
};

const getCommonPageProperties = (): AnalyticsProperties => {
  const page = getCurrentPageMetadata();
  return {
    ...getCommonEventProperties(),
    page_name: page.pageName,
    page_section: page.pageSection,
    page_path: getSanitizedPath(),
    page_route: page.pageRoute,
    page_title: page.pageName,
    page_url: `${window.location.origin}${getSanitizedPath()}`,
    page_query_keys: getPageQueryKeys(),
    page_referrer: lastTrackedReferrer || sanitizeReferrer(document.referrer),
  };
};

const cleanAnalyticsProperties = (properties: AnalyticsProperties): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    cleaned[key] = value;
  });

  return cleaned;
};

const getStableAnalyticsSignature = (name: string, properties: Record<string, unknown>): string =>
  JSON.stringify([
    name,
    Object.keys(properties)
      .sort()
      .map((key) => [key, properties[key]]),
  ]);

const safeMixpanelCall = (callback: () => void) => {
  try {
    callback();
  } catch (error) {
    if (MIXPANEL_DEBUG) {
      console.warn('Mixpanel tracking failed', error);
    }
  }
};

const isAnalyticsAllowed = (): boolean =>
  typeof window !== 'undefined' && !isDemoModeEnabled() && readCookieConsent()?.analytics === true;

const isMarketingAllowed = (): boolean =>
  typeof window !== 'undefined' && !isDemoModeEnabled() && readCookieConsent()?.marketing === true;

const optInMixpanelTracking = () => {
  safeMixpanelCall(() => {
    const trackingControls = mixpanel as MixpanelClient & {
      has_opted_in_tracking?: () => boolean;
      opt_in_tracking?: () => void;
    };
    if (!trackingControls.has_opted_in_tracking?.()) trackingControls.opt_in_tracking?.();
  });
};

const discardPendingMixpanelCalls = () => {
  pendingMixpanelCalls.length = 0;
  pendingIdentifiedUserSignature = '';
};

const ensureMixpanel = (): boolean => {
  if (typeof window === 'undefined' || !isAnalyticsAllowed()) {
    return false;
  }

  // Mixpanel is prod-only — dev/staging traffic (QA, internal testing) must
  // not count toward real product analytics.
  if (APP_ENVIRONMENT !== 'prod') {
    return false;
  }

  if (!MIXPANEL_TOKEN) {
    if (MIXPANEL_DEBUG && !missingMixpanelTokenWarned) {
      console.warn('Mixpanel token is missing. Set VITE_MIXPANEL_TOKEN to enable product analytics.');
      missingMixpanelTokenWarned = true;
    }
    return false;
  }

  if (!mixpanel) {
    if (!mixpanelImport) {
      // Download the optional SDK only after consent. Retain event/identity order
      // while it loads, including fast CTA clicks and auth transitions.
      mixpanelImport = import('mixpanel-browser')
        .then(({ default: client }) => {
          mixpanel = client;
          if (!ensureMixpanel()) {
            discardPendingMixpanelCalls();
            return;
          }
          const calls = pendingMixpanelCalls.splice(0);
          for (const callback of calls) {
            if (!isAnalyticsAllowed()) {
              pendingIdentifiedUserSignature = '';
              break;
            }
            safeMixpanelCall(() => callback(client));
          }
        })
        .catch((error) => {
          discardPendingMixpanelCalls();
          if (MIXPANEL_DEBUG) console.warn('Mixpanel SDK failed to load', error);
        })
        .finally(() => { mixpanelImport = null; });
    }
    return true;
  }

  if (mixpanelInitialized) {
    optInMixpanelTracking();
    return true;
  }

  safeMixpanelCall(() => {
    mixpanel.init(MIXPANEL_TOKEN, {
      autocapture: {
        click: false,
        dead_click: false,
        input: false,
        pageview: false,
        rage_click: false,
        scroll: false,
        submit: false,
        capture_text_content: false,
        capture_extra_attrs: [
          'data-analytics-id',
          'data-analytics-label',
          'data-analytics-section',
        ],
        block_selectors: ['.mp-no-track', '.mp-sensitive', '[data-analytics-ignore="true"]'],
      },
      batch_flush_interval_ms: MIXPANEL_BATCH_FLUSH_INTERVAL_MS,
      batch_requests: true,
      batch_size: MIXPANEL_BATCH_SIZE,
      debug: MIXPANEL_DEBUG,
      ...(MIXPANEL_API_HOST ? { api_host: MIXPANEL_API_HOST } : {}),
      ip: false,
      persistence: 'localStorage',
      track_pageview: false,
    });

    mixpanel.register(getCommonEventProperties());
    optInMixpanelTracking();
    mixpanelInitialized = true;
  });

  return mixpanelInitialized;
};

const callMixpanel = (
  callback: (client: MixpanelClient) => void,
  { preserveIdentity = false }: { preserveIdentity?: boolean } = {},
) => {
  if (!ensureMixpanel()) return;
  if (mixpanelInitialized && mixpanel) {
    safeMixpanelCall(() => callback(mixpanel));
  } else if (preserveIdentity || pendingMixpanelCalls.length < MAX_PENDING_MIXPANEL_CALLS) {
    // A burst of events must never drop an identify/reset and misattribute later events.
    pendingMixpanelCalls.push(callback);
  }
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      // Google's command queue consumes Arguments objects, as in its tag snippet.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
  }
};

const setGaDisabled = (disabled: boolean) => {
  if (GA_MEASUREMENT_ID) {
    (window as Window & Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = disabled;
  }
};

const getGoogleConsentPayload = (analyticsGranted: boolean, marketingGranted: boolean) => ({
  ad_personalization: marketingGranted ? 'granted' : 'denied',
  ad_storage: marketingGranted ? 'granted' : 'denied',
  ad_user_data: marketingGranted ? 'granted' : 'denied',
  analytics_storage: analyticsGranted ? 'granted' : 'denied',
});

const setDefaultGoogleConsent = () => {
  if (googleConsentDefaultSet || typeof window === 'undefined') {
    return;
  }

  ensureGtag();
  window.gtag?.('consent', 'default', {
    ...getGoogleConsentPayload(false, false),
    wait_for_update: 500,
  });
  googleConsentDefaultSet = true;
};

const updateGoogleConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }

  ensureGtag();
  window.gtag?.('consent', 'update', getGoogleConsentPayload(isAnalyticsAllowed(), isMarketingAllowed()));
};

type GoogleAnalyticsParamValue = string | number | boolean;

const GOOGLE_NAME_MAX_LENGTH = 40;
const GOOGLE_URL_PARAM_NAMES = new Set([
  'element_destination',
  'page_location',
  'page_path',
  'page_route',
  'page_url',
]);

const toGoogleName = (value: string, fallback: string): string => {
  const normalized = toSnakeCase(value).replace(/^[^a-z]+/, '');
  const safeName = normalized || fallback;
  const truncated = safeName.slice(0, GOOGLE_NAME_MAX_LENGTH).replace(/_+$/g, '');
  return truncated || fallback;
};

const toGoogleEventName = (eventName: string): string => toGoogleName(eventName, 'custom_event');

const toGoogleParamName = (paramName: string): string => toGoogleName(paramName, 'param');

const toGoogleParamValue = (
  paramName: string,
  value: unknown
): GoogleAnalyticsParamValue | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const serialized = value
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
      .join(',');
    return serialized ? sanitizeText(serialized, '').slice(0, 100) : undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    return GOOGLE_URL_PARAM_NAMES.has(paramName)
      ? trimmed.slice(0, 500)
      : sanitizeText(trimmed, '').slice(0, 100);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  return sanitizeText(value, '').slice(0, 100);
};

const toGoogleEventParams = (properties: Record<string, unknown>): Record<string, GoogleAnalyticsParamValue> => {
  const params: Record<string, GoogleAnalyticsParamValue> = {
    ...(GA_MEASUREMENT_ID ? { send_to: GA_MEASUREMENT_ID } : {}),
  };

  if (GA_DEBUG_MODE) {
    params.debug_mode = true;
  }

  Object.entries(properties).forEach(([key, value]) => {
    const paramName = toGoogleParamName(key);
    const paramValue = toGoogleParamValue(paramName, value);
    if (paramValue === undefined) {
      return;
    }

    params[paramName] = paramValue;
  });

  return params;
};

const configureGoogleAnalytics = (): boolean => {
  if (typeof window === 'undefined' || !isAnalyticsAllowed() || !GA_MEASUREMENT_ID) {
    return false;
  }

  ensureGtag();
  setDefaultGoogleConsent();
  setGaDisabled(false);
  updateGoogleConsent();
  appendScriptOnce(
    'caloriescience-google-analytics',
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`
  );

  if (!googleAnalyticsConfigured) {
    window.gtag?.('js', new Date());
    window.gtag?.('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      ...(GA_DEBUG_MODE ? { debug_mode: true } : {}),
    });
    googleAnalyticsConfigured = true;
  }

  return true;
};

const trackGooglePageView = () => {
  if (!configureGoogleAnalytics()) {
    return;
  }

  const pageParams = getPageViewParams();
  // Distinct resources can share the same sanitized route template.
  const pageKey = `${window.location.pathname}${window.location.search}|${pageParams.page_title}`;
  if (pageKey === lastTrackedGooglePageKey) {
    return;
  }

  window.gtag?.('config', GA_MEASUREMENT_ID, {
    ...pageParams,
    send_page_view: false,
    ...(GA_DEBUG_MODE ? { debug_mode: true } : {}),
  });
  window.gtag?.('event', 'page_view', toGoogleEventParams({
    ...pageParams,
    engagement_time_msec: 1,
  }));
  lastTrackedGooglePageKey = pageKey;
};

const trackGoogleProductEvent = (
  eventName: AnalyticsEventName | (string & {}),
  properties: Record<string, unknown>
) => {
  if (eventName === 'Page Viewed' || !configureGoogleAnalytics()) {
    return;
  }

  window.gtag?.('event', toGoogleEventName(eventName), toGoogleEventParams(properties));
};

const loadGoogleAnalytics = () => {
  trackGooglePageView();
};

const loadGoogleTagManager = () => {
  if (!GTM_CONTAINER_ID) {
    return;
  }

  if (document.getElementById('caloriescience-google-tag-manager')) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'gtm.js',
    'gtm.start': new Date().getTime(),
  });
  appendScriptOnce(
    'caloriescience-google-tag-manager',
    `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_CONTAINER_ID)}`
  );
};

const loadClarity = () => {
  if (!CLARITY_PROJECT_ID) {
    return;
  }

  if (typeof window.clarity !== 'function') {
    const clarityStub: ClarityFunction = (...args: unknown[]) => {
      clarityStub.q = clarityStub.q || [];
      clarityStub.q.push(args);
    };
    window.clarity = clarityStub;
  }

  appendScriptOnce(
    'caloriescience-clarity',
    `https://www.clarity.ms/tag/${encodeURIComponent(CLARITY_PROJECT_ID)}`
  );
  window.clarity?.('consent', true);
};

const ensureMetaPixel = (): boolean => {
  if (typeof window === 'undefined' || !isMarketingAllowed() || !META_PIXEL_ID) {
    return false;
  }

  if (typeof window.fbq !== 'function') {
    const fbq = ((...args: unknown[]) => {
      if (typeof fbq.callMethod === 'function') {
        fbq.callMethod(...args);
        return;
      }
      fbq.queue?.push(args);
    }) as MetaPixelFunction;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
  }

  window.fbq?.('consent', 'grant');
  appendScriptOnce('caloriescience-meta-pixel', 'https://connect.facebook.net/en_US/fbevents.js');

  if (!metaPixelConfigured) {
    window.fbq?.('init', META_PIXEL_ID);
    metaPixelConfigured = true;
  }

  return true;
};

const trackMetaPageView = () => {
  if (!ensureMetaPixel()) {
    return;
  }

  const pageKey = `${window.location.pathname}${window.location.search}`;
  if (pageKey === lastTrackedMetaPageKey) {
    return;
  }

  window.fbq?.('track', 'PageView');
  lastTrackedMetaPageKey = pageKey;
};

const ensureLinkedInInsight = (): boolean => {
  if (typeof window === 'undefined' || !isMarketingAllowed() || !LINKEDIN_PARTNER_ID) {
    return false;
  }

  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = Array.from(new Set([
    ...(window._linkedin_data_partner_ids || []),
    LINKEDIN_PARTNER_ID,
  ]));

  if (typeof window.lintrk !== 'function') {
    const lintrk = ((...args: unknown[]) => {
      lintrk.q = lintrk.q || [];
      lintrk.q.push(args);
    }) as LinkedInTrackFunction;
    window.lintrk = lintrk;
  }

  appendScriptOnce('caloriescience-linkedin-insight', 'https://snap.licdn.com/li.lms-analytics/insight.min.js');
  return true;
};

const normalizeLinkedInConversionId = (conversionId: string): string | number => {
  const numericId = Number(conversionId);
  return Number.isFinite(numericId) && conversionId.trim() === String(numericId)
    ? numericId
    : conversionId;
};

const trackLinkedInConversion = (conversionId?: string) => {
  if (!conversionId || !ensureLinkedInInsight()) {
    return;
  }

  window.lintrk?.('track', {
    conversion_id: normalizeLinkedInConversionId(conversionId),
  });
};

const trackMetaStandardEvent = (
  eventName: 'Lead' | 'StartTrial' | 'CompleteRegistration',
  properties: Record<string, unknown> = {},
  eventId?: string,
) => {
  if (!ensureMetaPixel()) {
    return;
  }

  window.fbq?.('track', eventName, properties, eventId ? { eventID: eventId } : undefined);
};

const deleteCookie = (name: string, domain?: string) => {
  const domainPart = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=; Max-Age=0; path=/${domainPart}; SameSite=Lax`;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}; SameSite=Lax`;
};

const clearMixpanelStorage = () => {
  const clearStorage = (storage: Storage) => {
    for (let index = storage.length - 1; index >= 0; index -= 1) {
      const key = storage.key(index);
      if (key?.startsWith('mp_')) {
        storage.removeItem(key);
      }
    }
  };

  try {
    clearStorage(window.localStorage);
    clearStorage(window.sessionStorage);
  } catch {
    // Storage access can fail in private mode; tracking must never break the app.
  }
};

export const clearAnalyticsCookies = (analytics = true, marketing = true) => {
  if (typeof document === 'undefined') {
    return;
  }

  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter(Boolean)
    .filter((name) =>
      [
        ...(analytics ? ['_ga', '_gid', '_gat', '_clck', '_clsk', 'mp_'] : []),
        ...(marketing ? ['_gcl', '_fbp', '_fbc', 'li_gc', 'li_fat_id'] : []),
      ].some((prefix) => name.startsWith(prefix))
    );

  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  const baseDomain =
    domainParts.length > 2 ? `.${domainParts.slice(domainParts.length - 2).join('.')}` : undefined;
  const domains = Array.from(new Set([undefined, hostname, `.${hostname}`, baseDomain]));

  for (const name of cookieNames) {
    for (const domain of domains) {
      deleteCookie(name, domain);
    }
  }

  if (analytics) clearMixpanelStorage();
};

const getElementType = (element: Element): string => {
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'a') return 'link';
  if (tagName === 'button') return 'button';
  const role = element.getAttribute('role');
  if (role) return role;
  return tagName;
};

const getInteractiveElement = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) {
    return null;
  }

  return target.closest<HTMLElement>(
    [
      'a[href]',
      'button',
      '[role="button"]',
      '[role="link"]',
      '[role="menuitem"]',
      '[role="menuitemcheckbox"]',
      '[role="menuitemradio"]',
      '[role="tab"]',
      '[role="option"]',
      '[role="checkbox"]',
      '[role="switch"]',
      '[data-analytics-event]',
      '[data-analytics-label]',
      '[data-analytics-id]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',')
  );
};

const shouldIgnoreElement = (element: Element): boolean =>
  Boolean(
    element.closest(
      [
        '.mp-no-track',
        '.mp-sensitive',
        '[data-analytics-ignore="true"]',
        '[data-analytics-sensitive="true"]',
        'input',
        'textarea',
        'select',
        '[contenteditable="true"]',
      ].join(',')
    )
  );

const getElementLabel = getAnalyticsElementLabel;
const getElementSection = getAnalyticsElementSection;

const getElementDestination = (element: HTMLElement): string | undefined => {
  if (!(element instanceof HTMLAnchorElement)) {
    return undefined;
  }

  try {
    const destination = new URL(element.href, window.location.origin);
    if (destination.origin === window.location.origin) {
      return getSanitizedPath(destination.pathname, destination.search);
    }
    return destination.hostname;
  } catch {
    return undefined;
  }
};

const trackRageClickIfNeeded = (event: MouseEvent, element: HTMLElement | null, signature: string) => {
  const now = Date.now();
  const maxDistance = 30;
  const timeoutMs = 1200;
  const distance = clickBurst
    ? Math.hypot(event.clientX - clickBurst.lastX, event.clientY - clickBurst.lastY)
    : Number.POSITIVE_INFINITY;

  if (!clickBurst || clickBurst.signature !== signature || now - clickBurst.lastAt > timeoutMs || distance > maxDistance) {
    clickBurst = {
      count: 1,
      firstAt: now,
      lastAt: now,
      lastX: event.clientX,
      lastY: event.clientY,
      signature,
      tracked: false,
    };
    return;
  }

  clickBurst.count += 1;
  clickBurst.lastAt = now;
  clickBurst.lastX = event.clientX;
  clickBurst.lastY = event.clientY;

  if (clickBurst.count >= 4 && !clickBurst.tracked) {
    clickBurst.tracked = true;
    trackProductEvent('Rage Clicked', {
      ...getCommonPageProperties(),
      element_label: element ? getElementLabel(element) : 'Unknown',
      element_section: element ? getElementSection(element) : 'unknown',
      element_type: element ? getElementType(element) : 'unknown',
      rage_click_count: clickBurst.count,
      rage_click_duration_ms: now - clickBurst.firstAt,
    });
  }
};

const trackDeadClickIfNeeded = (element: HTMLElement | null, beforeMutationVersion: number, beforePath: string) => {
  if (!element || element instanceof HTMLAnchorElement || element.closest('form')) {
    return;
  }

  window.setTimeout(() => {
    if (window.location.pathname !== beforePath || domMutationVersion !== beforeMutationVersion) {
      return;
    }

    trackProductEvent('Dead Clicked', {
      ...getCommonPageProperties(),
      element_label: getElementLabel(element),
      element_section: getElementSection(element),
      element_type: getElementType(element),
    });
  }, 700);
};

const handleDocumentClick = (event: MouseEvent) => {
  // Ignore the original target too: a sensitive child may be inside a tracked button.
  if (event.target instanceof Element && shouldIgnoreElement(event.target)) return;
  const element = getInteractiveElement(event.target);
  if (!element || shouldIgnoreElement(element)) return;

  if (isMarketingAllowed() && element instanceof HTMLAnchorElement &&
      ['/', '/start', '/start/'].includes(window.location.pathname) &&
      element.pathname.replace(/\/$/, '') === '/signup') {
    pushAdvertisingEvent({
      event: 'cs_landing_cta_click',
      page_path: window.location.pathname,
      cta_label: getElementLabel(element),
      cta_id: element.dataset.analyticsId,
    });
  }

  if (!isAnalyticsAllowed()) return;

  const label = getElementLabel(element);
  const section = getElementSection(element);
  const type = getElementType(element);
  const signature = `${window.location.pathname}|${type}|${section}|${label}|${Math.round(event.clientX / 10)}|${Math.round(event.clientY / 10)}`;
  const now = Date.now();
  const beforeMutationVersion = domMutationVersion;
  const beforePath = window.location.pathname;

  trackRageClickIfNeeded(event, element, signature);
  trackDeadClickIfNeeded(element, beforeMutationVersion, beforePath);

  if (signature === lastClickSignature && now - lastClickAt < 250) {
    return;
  }

  lastClickSignature = signature;
  lastClickAt = now;

  const explicitEventName = element.dataset.analyticsEvent as AnalyticsEventName | undefined;
  const page = getCurrentPageMetadata();
  const derivedEventName = explicitEventName && explicitEventName !== 'Landing CTA Clicked'
    ? explicitEventName
    : getAnalyticsClickEventName(page.pageName, label);

  trackProductEvent(derivedEventName, {
    ...getCommonPageProperties(),
    action_name: label,
    event_category: 'UI Interaction',
    element_destination: getElementDestination(element),
    element_event_name: explicitEventName,
    element_label: label,
    element_role: element.getAttribute('role') || undefined,
    element_section: section,
    element_tracking_id: element.dataset.analyticsId,
    element_type: type,
  });
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown error';
};

const getErrorSourcePath = (filename: string): string | undefined => {
  if (!filename) {
    return undefined;
  }

  try {
    return sanitizePathname(new URL(filename, window.location.origin).pathname);
  } catch {
    return undefined;
  }
};

const handleWindowError = (event: ErrorEvent) => {
  trackProductEvent('JavaScript Error', {
    ...getCommonPageProperties(),
    error_message: sanitizeText(event.message, 'Unknown error'),
    error_name: event.error instanceof Error ? event.error.name : 'Error',
    error_source: getErrorSourcePath(event.filename),
  });
};

const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
  trackProductEvent('JavaScript Error', {
    ...getCommonPageProperties(),
    error_message: sanitizeText(getErrorMessage(event.reason), 'Unhandled promise rejection'),
    error_name: event.reason instanceof Error ? event.reason.name : 'UnhandledRejection',
  });
};

const bindGlobalAnalyticsListeners = () => {
  if (globalListenersBound || typeof window === 'undefined') {
    return;
  }

  globalListenersBound = true;
  document.addEventListener('click', handleDocumentClick, true);
  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  try {
    const observer = new MutationObserver(() => {
      domMutationVersion += 1;
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  } catch {
    // MutationObserver may be unavailable in older browsers; dead-click tracking is best effort.
  }
};

const getSettingsSectionContext = (): AnalyticsProperties => {
  const params = new URLSearchParams(window.location.search);
  const view = params.get('view')?.trim() || '';
  const tab = params.get('tab')?.trim() || '';
  const subsection = params.get('section')?.trim() || '';
  const viewToSection: Record<string, string> = {
    hub: 'hub',
    'profile-details': 'profile_details',
    'practice-info': 'practice_info',
    notifications: 'notifications',
    security: 'security',
    'plans-packages': 'plans_packages',
    'subscription-plan': 'subscription_plan',
    'upgrade-plan': 'upgrade_plan',
    'invoice-tax-details': 'invoice_tax_details',
  };
  const tabToSection: Record<string, string> = {
    availability: 'availability',
    billing: 'subscription_plan',
    feedback: 'feedback',
    settings: 'profile_details',
  };
  const knownTab = Object.prototype.hasOwnProperty.call(tabToSection, tab);
  const knownView = Object.prototype.hasOwnProperty.call(viewToSection, view);
  const settingsSection =
    (knownTab && tabToSection[tab]) ||
    (knownView && viewToSection[view]) ||
    'profile_details';

  return {
    product_area: 'settings',
    settings_section: settingsSection,
    settings_subsection: tab === 'availability'
      ? (['weekly', 'breaks', 'timeoff'].includes(subsection) ? subsection : 'weekly')
      : undefined,
    settings_tab: knownTab ? tab : undefined,
    settings_view: knownView ? toSnakeCase(view) : undefined,
  };
};

const getClientDetailsSection = (pathname: string): string | null => {
  const detailsMatch = pathname.match(/^\/clients\/[^/]+\/details-v2(?:\/([^/]+))?/);
  if (detailsMatch) {
    const page = getAnalyticsPageMetadata(pathname);
    return toSnakeCase(page.pageRoute.split('/').pop() || 'overview');
  }

  if (/^\/clients\/[^/]+\/?$/.test(pathname)) {
    return 'legacy_overview';
  }

  return null;
};

const getMealPlanSurface = (pathname: string): string | null => {
  if (pathname === '/meal-planning') return 'options';
  if (pathname === '/generic-meal-plans') return 'generic_library';
  if (/^\/meal-planning\/generic\/[^/]+\/nutrition\/[^/]+/.test(pathname)) return 'generic_nutrition';
  if (/^\/meal-planning\/client\/[^/]+\/meal-plan/.test(pathname)) return 'client_meal_plans';
  if (pathname === '/ai-meal-planning') return 'ai_create';
  if (pathname === '/manual-meal-plan') return 'manual_create';
  if (pathname === '/auto-meal-planning') return 'auto_create';
  if (/^\/manual-meal-plan\/[^/]+\/client\/[^/]+/.test(pathname)) return 'manual_draft';
  if (/^\/ai-meal-plan\/draft\/[^/]+\/client\/[^/]+\/recipe\/[^/]+/.test(pathname)) return 'ai_recipe_details';
  if (/^\/ai-meal-plan\/draft\/[^/]+\/client\/[^/]+/.test(pathname)) return 'ai_draft';
  if (/^\/automated-meal-plan\/draft\/[^/]+\/client\/[^/]+/.test(pathname)) return 'automated_draft';
  if (/^\/finalized-meal-plan\/[^/]+\/client\/[^/]+/.test(pathname)) return 'finalized_view';
  if (/^\/meal-plans\/generic\/[^/]+\/nutrition\/[^/]+/.test(pathname)) return 'generic_nutrition';
  if (/^\/meal-plans\/[^/]+\/clients\/[^/]+\/nutrition\/[^/]+/.test(pathname)) return 'nutrition_analysis';
  if (pathname === '/templates') return 'templates';
  return null;
};

const getFocusedRouteProperties = (pathname: string): AnalyticsProperties | null => {
  if (pathname === '/profile') {
    return getSettingsSectionContext();
  }

  if (pathname === '/clients/add' || pathname === '/admin/client-invites') {
    return {
      onboarding_mode: pathname === '/admin/client-invites' ? 'admin_invite' : 'client_add',
      onboarding_step: 'route_open',
      product_area: 'client_onboarding',
    };
  }

  const clientDetailsSection = getClientDetailsSection(pathname);
  if (clientDetailsSection) {
    return {
      client_details_section: clientDetailsSection,
      client_details_version: clientDetailsSection === 'legacy_overview' ? 'legacy' : 'v2',
      product_area: 'client_details',
    };
  }

  const mealPlanSurface = getMealPlanSurface(pathname);
  if (mealPlanSurface) {
    return {
      meal_plan_surface: mealPlanSurface,
      product_area: 'meal_plans',
    };
  }

  return null;
};

const trackMixpanelPageView = () => {
  if (!ensureMixpanel()) {
    return;
  }

  const pageKey = `${window.location.pathname}${window.location.search}`;
  if (pageKey === lastTrackedPageKey) {
    return;
  }

  trackProductEvent('Page Viewed', {
    ...getCommonPageProperties(),
    ...getFocusedRouteProperties(window.location.pathname),
  });
  lastTrackedPageKey = pageKey;
  lastTrackedReferrer = getSanitizedPath();
};

const disableProductAnalytics = () => {
  setGaDisabled(true);
  lastTrackedGooglePageKey = '';
  lastTrackedPageKey = '';
  lastTrackedEventSignature = '';
  lastTrackedEventAt = 0;
  lastIdentifiedUserSignature = '';
  discardPendingMixpanelCalls();
  window.clarity?.('consent', false);
  if (mixpanelInitialized) {
    safeMixpanelCall(() => {
      const trackingControls = mixpanel as MixpanelClient & {
        opt_out_tracking?: () => void;
      };
      mixpanel.reset();
      trackingControls.opt_out_tracking?.();
    });
  }
  clearAnalyticsCookies(true, false);
};

const disableMarketing = () => {
  window.fbq?.('consent', 'revoke');
  lastTrackedMetaPageKey = '';
  clearAnalyticsCookies(false, true);
};

export const loadAnalytics = () => {
  if (typeof window === 'undefined') return;
  setDefaultGoogleConsent();
  updateGoogleConsent();
  const analytics = isAnalyticsAllowed();
  const marketing = isMarketingAllowed();
  // GA, Clarity and Mixpanel are loaded directly; this container owns Ads tags.
  if (marketing) loadGoogleTagManager();
  if (analytics || marketing) {
    bindGlobalAnalyticsListeners();
  }
  if (analytics) {
    loadGoogleAnalytics();
    loadClarity();
    ensureMixpanel();
    trackMixpanelPageView();
  } else {
    disableProductAnalytics();
  }
  if (marketing) {
    trackMetaPageView();
    ensureLinkedInInsight();
  } else {
    disableMarketing();
  }
  if (window.location.pathname.replace(/\/$/, '') !== '/signup') signupFormView = null;
  flushSignupFormView();
  flushPendingSignupConversions();
};

export const disableAnalytics = () => {
  if (typeof window === 'undefined') return;
  setDefaultGoogleConsent();
  updateGoogleConsent();
  disableProductAnalytics();
  disableMarketing();
  if (readCookieConsent()) clearPendingSignupConversions();
};

export const trackProductEvent = (
  eventName: AnalyticsEventName | (string & {}),
  properties: AnalyticsProperties = {}
) => {
  if (!isAnalyticsAllowed()) {
    return;
  }

  const payload = cleanAnalyticsProperties({
    ...getCommonPageProperties(),
    ...getAttributionEventProperties(),
    ...properties,
    ...(eventName === 'Page Viewed' ? { event_category: 'Page View' } : {}),
  });
  const eventSignature = getStableAnalyticsSignature(String(eventName), payload);
  const now = Date.now();
  if (eventName !== 'Page Viewed' && eventSignature === lastTrackedEventSignature && now - lastTrackedEventAt < TRACK_DEDUPE_WINDOW_MS) {
    return;
  }
  lastTrackedEventSignature = eventSignature;
  lastTrackedEventAt = now;

  trackGoogleProductEvent(eventName, payload);

  callMixpanel((client) => {
    // Keep GA's single standard page_view while making the Mixpanel event feed readable.
    const mixpanelEventName = eventName === 'Page Viewed'
      ? `${payload.page_name} Viewed`
      : eventName;
    client.track(mixpanelEventName, payload);
    if (MIXPANEL_DEBUG) {
      console.info('[analytics]', mixpanelEventName, payload);
    }
  });
};

const pushAdvertisingEvent = (payload: Record<string, unknown>) => {
  if (!isMarketingAllowed()) return;
  setDefaultGoogleConsent();
  updateGoogleConsent();
  loadGoogleTagManager();
  window.dataLayer?.push(payload);
  // Do not let a later unrelated GTM event inherit enhanced-conversion data.
  if (payload.user_data) window.dataLayer?.push({ user_data: null });
};

let signupFormView: { analyticsSent: boolean; marketingSent: boolean } | null = null;

const flushSignupFormView = () => {
  if (!signupFormView) return;
  if (isAnalyticsAllowed() && !signupFormView.analyticsSent) {
    trackProductEvent('Signup Started', { signup_source: 'signup_form' });
    signupFormView.analyticsSent = true;
  }
  if (isMarketingAllowed() && !signupFormView.marketingSent) {
    if (ensureMetaPixel()) window.fbq?.('trackCustom', 'Signup_Start');
    pushAdvertisingEvent({ event: 'cs_signup_started' });
    signupFormView.marketingSent = true;
  }
};

export const trackSignupFormViewed = () => {
  signupFormView ??= { analyticsSent: false, marketingSent: false };
  flushSignupFormView();
};

export const trackLandingLeadConversion = (properties: AnalyticsProperties = {}) => {
  if (typeof window === 'undefined' || !isMarketingAllowed()) {
    return;
  }

  const leadIntent = typeof properties.lead_intent === 'string' ? properties.lead_intent : undefined;
  const contentName = leadIntent === 'book_demo' ? 'Book a Demo' : 'Landing Contact Form';

  trackMetaStandardEvent('Lead', {
    content_category: 'landing_contact_form',
    content_name: contentName,
    lead_intent: leadIntent,
  });

  if (leadIntent === 'book_demo') {
    trackLinkedInConversion(LINKEDIN_BOOK_DEMO_CONVERSION_ID);
  }

  sendServerEvent('Landing Contact Form Submitted', properties);
};

export const trackSignupMarketingConversion = (
  properties: AnalyticsProperties,
  identity: { userId: string; email?: string },
) => {
  if (isDemoModeEnabled()) return;
  queueSignupConversion(identity, properties, {
    trackProduct: (payload) => trackProductEvent('Signup Completed', payload),
    trackMeta: (event, eventId, payload) => {
      trackMetaStandardEvent(event, payload, eventId);
      sendServerEvent(event, payload, { eventId });
      if (event === 'StartTrial') trackLinkedInConversion(LINKEDIN_SIGNUP_CONVERSION_ID);
    },
    trackGoogle: pushAdvertisingEvent,
  });
};

export const trackAnalyticsPageView = () => {
  loadAnalytics();
};

const getEmailDomain = (email: unknown): string | undefined => {
  if (typeof email !== 'string') {
    return undefined;
  }

  const domain = email.split('@')[1]?.trim().toLowerCase();
  return domain || undefined;
};

const getUserProfileProperties = (user: Partial<User> & Record<string, unknown>): AnalyticsProperties => {
  const roles = Array.isArray(user.roles)
    ? user.roles.map((role) => String(role).trim()).filter(Boolean)
    : user.role
      ? [String(user.role)]
      : [];

  const name = typeof user.fullName === 'string' ? user.fullName.trim() : undefined;
  const email = typeof user.email === 'string' ? user.email.trim() : undefined;

  return {
    // Mixpanel reserved profile properties: populate the Name/Email columns and
    // are usable as breakdown dimensions in reports.
    $name: name || undefined,
    $email: email || undefined,
    account_type: typeof user.accountType === 'string' ? user.accountType : undefined,
    auth_provider: typeof user.authProvider === 'string' ? user.authProvider : undefined,
    ...getAnalyticsCountryProperties(user.country),
    email_domain: getEmailDomain(user.email),
    has_practice_name: Boolean(user.practiceName),
    has_profile_image: Boolean(user.profileImageUrl),
    preferred_measurement_system:
      typeof user.preferredMeasurementSystem === 'string' ? user.preferredMeasurementSystem : undefined,
    timezone: typeof user.timezone === 'string' ? user.timezone : undefined,
    user_role: roles[0],
    user_roles: roles,
  };
};

export const identifyAnalyticsUser = (user: Partial<User> & Record<string, unknown>) => {
  if (!user.id || !ensureMixpanel()) {
    return;
  }

  const profileProperties = getUserProfileProperties(user);
  const cleanedProfileProperties = cleanAnalyticsProperties(profileProperties);
  const registeredProperties = cleanAnalyticsProperties({
    user_role: profileProperties.user_role,
    user_roles: profileProperties.user_roles,
    ...getAnalyticsCountryProperties(user.country),
  });
  const identitySignature = getStableAnalyticsSignature(String(user.id), {
    ...cleanedProfileProperties,
    ...registeredProperties,
  });
  if (identitySignature === lastIdentifiedUserSignature || identitySignature === pendingIdentifiedUserSignature) {
    return;
  }

  // Snapshot identity and attribution at the call site, before routes can change.
  const userId = String(user.id);
  const firstTouch = cleanAnalyticsProperties(getAttributionEventProperties('first_touch'));
  const missingCountryKeys = Object.keys(getAnalyticsCountryProperties(user.country))
    .filter((key) => profileProperties[key] === undefined);
  pendingIdentifiedUserSignature = identitySignature;
  callMixpanel((client) => {
    try {
      client.identify(userId);
      // Clear old country properties before registering the captured account country.
      missingCountryKeys.forEach((key) => client.unregister(key));
      if (missingCountryKeys.length) client.people.unset(missingCountryKeys);
      client.people.set(cleanedProfileProperties);
      const people = client.people as typeof client.people & {
        set_once?: (properties: Record<string, unknown>) => void;
      };
      people.set_once?.(firstTouch);
      client.register(registeredProperties as Record<string, unknown>);
      // A queued identity is successful only after the SDK accepts it.
      lastIdentifiedUserSignature = identitySignature;
    } finally {
      if (pendingIdentifiedUserSignature === identitySignature) {
        pendingIdentifiedUserSignature = '';
      }
    }
  }, { preserveIdentity: true });
};

export const resetAnalyticsUser = () => {
  lastIdentifiedUserSignature = '';
  pendingIdentifiedUserSignature = '';
  if (!mixpanelInitialized && pendingMixpanelCalls.length === 0) {
    return;
  }

  const commonProperties = getCommonEventProperties();
  callMixpanel((client) => {
    client.reset();
    // Earlier queued identities may have completed since reset was requested.
    lastIdentifiedUserSignature = '';
    client.register(commonProperties);
  }, { preserveIdentity: true });
};

const normalizeApiRoute = (url?: string): string => {
  if (!url) {
    return 'unknown';
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return sanitizePathname(parsed.pathname);
  } catch {
    return sanitizePathname(url.split('?')[0] || 'unknown');
  }
};

const routeMatches = (route: string, pattern: string): boolean => {
  const routeSegments = route.split('/').filter(Boolean);
  const patternSegments = pattern.split('/').filter(Boolean);
  if (routeSegments.length !== patternSegments.length) {
    return false;
  }

  return patternSegments.every((segment, index) => segment.startsWith(':') || segment === routeSegments[index]);
};

const routeIn = (route: string, patterns: string[]): boolean =>
  patterns.some((pattern) => routeMatches(route, pattern));

const getApiProductArea = (route: string): string => {
  if (route.startsWith('/meal-plans')) return 'meal_plans';
  if (route.startsWith('/clients')) return 'clients';
  if (route.startsWith('/nutritionists') || route.startsWith('/availability') || route.startsWith('/auth/change-password')) {
    return 'settings';
  }
  if (route.startsWith('/appointments')) return 'appointments';
  if (route.startsWith('/invoices') || route.startsWith('/client-invoices')) return 'billing';
  if (route.startsWith('/questionnaires')) return 'questionnaires';
  return 'unknown';
};

const buildApiProperties = ({ durationMs, method, status, url }: ApiTrackingInput): AnalyticsProperties => {
  const apiRoute = normalizeApiRoute(url);
  return {
    ...getCommonPageProperties(),
    api_duration_ms: Number.isFinite(durationMs) ? Math.round(durationMs ?? 0) : undefined,
    api_method: method?.toUpperCase(),
    api_route: apiRoute,
    api_status: status,
    product_area: getApiProductArea(apiRoute),
  };
};

export const trackApiFailure = (input: ApiFailureTrackingInput) => {
  const status = input.status ?? 0;
  const failureGroup =
    status === 0
      ? 'network'
      : status === 401 || status === 403
        ? 'auth'
        : status >= 500
          ? 'server'
          : 'client';

  trackProductEvent('API Request Failed', {
    ...buildApiProperties(input),
    api_error_code: input.code,
    api_error_message: input.message ? sanitizeText(input.message, 'Request failed') : undefined,
    api_failure_group: failureGroup,
  });
};

export const trackApiSuccessEvent = (input: ApiTrackingInput) => {
  const method = input.method?.toUpperCase() || 'GET';
  const route = normalizeApiRoute(input.url);
  const baseProperties = buildApiProperties(input);
  const currentSettingsContext = window.location.pathname === '/profile' ? getSettingsSectionContext() : {};

  if (method === 'POST' && route === '/clients') {
    trackProductEvent('Client Created', baseProperties);
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/clients/:id')) {
    trackProductEvent('Client Details Updated', {
      ...baseProperties,
      client_details_section: getClientDetailsSection(window.location.pathname) ?? undefined,
      product_area: 'client_details',
    });
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/clients/:id')) {
    trackProductEvent('Client Deleted', {
      ...baseProperties,
      product_area: 'clients',
    });
    return;
  }
  if (method === 'POST' && routeMatches(route, '/clients/:id/notes')) {
    trackProductEvent('Client Note Added', {
      ...baseProperties,
      product_area: 'client_details',
    });
    return;
  }
  if (method === 'POST' && routeMatches(route, '/clients/:id/lab-reports/extract')) {
    trackProductEvent('Client Lab Report Extraction Started', {
      ...baseProperties,
      product_area: 'client_details',
    });
    return;
  }
  if (method === 'PATCH' && routeMatches(route, '/clients/:id/lab-reports/notes')) {
    trackProductEvent('Client Lab Report Notes Updated', {
      ...baseProperties,
      product_area: 'client_details',
    });
    return;
  }
  if (method === 'PATCH' && routeMatches(route, '/clients/:id/lab-reports/:id')) {
    trackProductEvent('Client Lab Report Updated', {
      ...baseProperties,
      product_area: 'client_details',
    });
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/clients/:id/lab-reports/:id')) {
    trackProductEvent('Client Lab Report Deleted', {
      ...baseProperties,
      product_area: 'client_details',
    });
    return;
  }

  if (method === 'POST' && (route === '/questionnaires/assign' || routeMatches(route, '/questionnaires/:id/assign'))) {
    trackProductEvent('Questionnaire Assigned', baseProperties);
    return;
  }
  if (method === 'POST' && route === '/appointments') {
    trackProductEvent('Appointment Created', baseProperties);
    return;
  }
  if (method === 'POST' && (route === '/client-invoices' || route === '/invoices')) {
    trackProductEvent('Invoice Created', baseProperties);
    return;
  }

  if (method === 'PUT' && route === '/nutritionists/profile') {
    const settingsSection = currentSettingsContext.settings_section;
    trackProductEvent(settingsSection === 'practice_info' ? 'Practice Information Saved' : 'Practitioner Profile Saved', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'PUT' && route === '/nutritionists/profile-pref') {
    trackProductEvent('Profile Preferences Saved', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'POST' && route === '/nutritionists/profile/upload-url') {
    trackProductEvent('Settings Profile Photo Upload Started', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'DELETE' && route === '/nutritionists/profile/photo') {
    trackProductEvent('Settings Profile Photo Deleted', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'PUT' && route === '/nutritionists/invoice-settings') {
    trackProductEvent('Invoice Settings Saved', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'POST' && route === '/auth/change-password') {
    trackProductEvent('Settings Password Changed', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'POST' && route === '/nutritionists/consultation-types') {
    trackProductEvent('Settings Consultation Type Created', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/nutritionists/consultation-types/:id')) {
    trackProductEvent('Settings Consultation Type Updated', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/nutritionists/consultation-types/:id')) {
    trackProductEvent('Settings Consultation Type Deleted', {
      ...baseProperties,
      ...currentSettingsContext,
      product_area: 'settings',
    });
    return;
  }
  if (method === 'PUT' && route === '/availability/schedule') {
    trackProductEvent('Weekly Availability Saved', {
      ...baseProperties,
      ...currentSettingsContext,
      settings_section: 'availability_weekly',
      product_area: 'settings',
    });
    return;
  }
  if (method === 'POST' && route === '/availability/breaks') {
    trackProductEvent('Recurring Availability Break Created', {
      ...baseProperties,
      ...currentSettingsContext,
      settings_section: 'availability_breaks',
      product_area: 'settings',
    });
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/availability/breaks/:id')) {
    trackProductEvent('Recurring Availability Break Updated', {
      ...baseProperties,
      ...currentSettingsContext,
      settings_section: 'availability_breaks',
      product_area: 'settings',
    });
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/availability/breaks/:id')) {
    trackProductEvent('Recurring Availability Break Deleted', {
      ...baseProperties,
      ...currentSettingsContext,
      settings_section: 'availability_breaks',
      product_area: 'settings',
    });
    return;
  }
  if (method === 'POST' && route === '/availability/time-off') {
    trackProductEvent('Settings Time Off Created', {
      ...baseProperties,
      ...currentSettingsContext,
      settings_section: 'availability_timeoff',
      product_area: 'settings',
    });
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/availability/time-off/:id')) {
    trackProductEvent('Settings Time Off Deleted', {
      ...baseProperties,
      ...currentSettingsContext,
      settings_section: 'availability_timeoff',
      product_area: 'settings',
    });
    return;
  }

  if (method === 'POST' && route === '/meal-plans/generate') {
    trackProductEvent('Automated Meal Plan Generation Started', baseProperties);
    return;
  }
  if (method === 'POST' && route === '/meal-plans/generate-ai') {
    trackProductEvent('AI Meal Plan Generation Started', baseProperties);
    return;
  }
  if (method === 'POST' && route === '/meal-plans/manual/create') {
    trackProductEvent('Manual Meal Plan Created', baseProperties);
    return;
  }
  if (method === 'POST' && route === '/meal-plans/draft') {
    trackProductEvent('Meal Plan Draft Created', baseProperties);
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/meal-plans/drafts/:id')) {
    trackProductEvent('AI Meal Plan Draft Updated', baseProperties);
    return;
  }
  if (method === 'POST' && routeMatches(route, '/meal-plans/drafts/:id/shuffle')) {
    trackProductEvent('AI Meal Plan Shuffled', baseProperties);
    return;
  }
  if (method === 'POST' && routeMatches(route, '/meal-plans/drafts/:id/smart-edit')) {
    trackProductEvent('AI Meal Plan Smart Edit Applied', baseProperties);
    return;
  }
  if (
    method === 'POST' &&
    routeIn(route, [
      '/meal-plans/drafts/:id/finalize',
      '/meal-plans/manual/:id/finalize',
    ])
  ) {
    trackProductEvent('Meal Plan Finalized', baseProperties);
    return;
  }
  if (method === 'POST' && route === '/meal-plans/generic/create') {
    trackProductEvent('Cohort Meal Plan Created', baseProperties);
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/meal-plans/generic/:id')) {
    trackProductEvent('Cohort Meal Plan Updated', baseProperties);
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/meal-plans/generic/:id')) {
    trackProductEvent('Cohort Meal Plan Deleted', baseProperties);
    return;
  }
  if (method === 'POST' && route === '/meal-plans/generic/assign') {
    trackProductEvent('Cohort Meal Plan Assigned', baseProperties);
    return;
  }
  if (method === 'POST' && routeIn(route, ['/meal-plans/generic/import', '/meal-plans/generic/:id/import'])) {
    trackProductEvent('Meal Plan Template Imported', baseProperties);
    return;
  }
  if (method === 'POST' && routeIn(route, ['/meal-plans/drafts/:id/template', '/meal-plans/:id/template'])) {
    trackProductEvent('Meal Plan Template Saved', baseProperties);
    return;
  }
  if (method === 'POST' && routeIn(route, ['/meal-plans/drafts/:id/meal-slot/template', '/meal-plans/:id/meal-slot/template'])) {
    trackProductEvent('Meal Slot Template Saved', baseProperties);
    return;
  }
  if (method === 'POST' && routeMatches(route, '/meal-plans/templates/:id/use')) {
    trackProductEvent('Meal Plan Template Applied', baseProperties);
    return;
  }
  if (
    method === 'POST' &&
    routeIn(route, [
      '/meal-plans/manual/add-item',
      '/meal-plans/:id/add-item',
      '/meal-plans/generic/add-item',
    ])
  ) {
    trackProductEvent('Meal Plan Item Added', baseProperties);
    return;
  }
  if (
    method === 'POST' &&
    routeIn(route, [
      '/meal-plans/manual/add-recipe',
      '/meal-plans/manual/add-recipes',
      '/meal-plans/generic/add-recipe',
      '/meal-plans/generic/add-recipes',
    ])
  ) {
    trackProductEvent('Meal Plan Recipe Added', baseProperties);
    return;
  }
  if (
    method === 'POST' &&
    routeIn(route, [
      '/meal-plans/manual/meal-slot',
      '/meal-plans/generic/meal-slot',
    ])
  ) {
    trackProductEvent('Meal Plan Slot Added', baseProperties);
    return;
  }
  if (
    method === 'PUT' &&
    routeIn(route, [
      '/meal-plans/manual/update-ingredient',
      '/meal-plans/manual/update-servings',
      '/meal-plans/:id/update-item',
      '/meal-plans/:id/customize-item',
      '/meal-plans/generic/update-servings',
      '/meal-plans/generic/update-ingredient',
      '/meal-plans/generic/:id/update-item',
    ])
  ) {
    trackProductEvent('Meal Plan Item Updated', baseProperties);
    return;
  }
  if (
    method === 'DELETE' &&
    routeIn(route, [
      '/meal-plans/manual/remove-item',
      '/meal-plans/:id/remove-item',
      '/meal-plans/generic/remove-item',
    ])
  ) {
    trackProductEvent('Meal Plan Item Removed', baseProperties);
    return;
  }
  if (method === 'DELETE' && route === '/meal-plans/manual/remove-recipe') {
    trackProductEvent('Meal Plan Recipe Removed', baseProperties);
    return;
  }
  if (method === 'POST' && routeMatches(route, '/meal-plans/:id/copy-day')) {
    trackProductEvent('Meal Plan Day Copied', baseProperties);
    return;
  }
  if (method === 'DELETE' && routeMatches(route, '/meal-plans/:id/day/:dayNumber')) {
    trackProductEvent('Meal Plan Day Removed', baseProperties);
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/meal-plans/:id/day-name')) {
    trackProductEvent('Meal Plan Day Renamed', baseProperties);
    return;
  }
  if (method === 'PUT' && routeMatches(route, '/meal-plans/:id/change-dates')) {
    trackProductEvent('Meal Plan Dates Changed', baseProperties);
    return;
  }
  if (
    method === 'PUT' &&
    routeIn(route, [
      '/meal-plans/:id/reorder-days',
      '/meal-plans/:id/reorder-meals',
      '/meal-plans/:id/swap-meals',
    ])
  ) {
    trackProductEvent('Meal Plan Reordered', baseProperties);
  }
};
